import { bookingInquiryStrings } from '@data/bookingInquiry';
import { cargosLimitByTruckOptions, priceSimulatorStrings } from '@data/priceSimulator';
import { TruckName } from '@models/next/BookingInquiryEnums';
import { BookingInquiryCargoForm } from '@models/next/BookingInquiryForms';
import { BookingInquiryHelper } from '@models/next/BookingInquiryHelper';
import { RoutePlanSimulator } from '@models/next/RoutePlanSimulator';
import { f } from '@utils/nextForms';
import { pipe, sumBy } from 'remeda';
import { z } from 'zod';

export const PriceSimulatorTruckName = z.enum(['any', ...TruckName.options]);

export type PriceSimulatorTruckName = z.infer<typeof PriceSimulatorTruckName>;

export const PriceSimulatorPickupAddressLoose = z.object({
  zip_code: z.string(),
  city_name: z.string(),
  /**
   * To work with `useCNZipCodeAutoComplete`, which requires a third field for CN addresses
   */
  dummy_address_cn: z.string(),
});

export const PriceSimulatorPickupAddress = z.object({
  zip_code: f.withChineseZipcodeRegex(z.string().min(1)),
  city_name: z.string().min(1),
});

export type PriceSimulatorPickupAddress = z.infer<typeof PriceSimulatorPickupAddress>;

export const PriceSimulatorReceiver = z.object({
  zip_code: f.withJapaneseZipcodeRegex(z.string()),
  pref: z.string().min(1),
  city_cd: z.string().min(1),
  city: z.string(),
});

export type PriceSimulatorReceiver = z.infer<typeof PriceSimulatorReceiver>;

export const PriceSimulatorTruckType = z.enum(['any', ...TruckName.options]);

export type PriceSimulatorTruckType = z.infer<typeof PriceSimulatorTruckType>;

export const PriceSimulatorForm = z
  .object({
    has_pickups: z.boolean(),
    cargos_port_name: z.string(),
    receiver: PriceSimulatorReceiver,
    pickup_addresses: PriceSimulatorPickupAddressLoose.array(),
    cargos: BookingInquiryCargoForm.array().min(1),
    truck_type: PriceSimulatorTruckName,
  })
  .superRefine((v, ctx) => {
    pipe(
      v.cargos.map((c) => BookingInquiryHelper.getCargoSummary(c)),
      (summaries) => {
        if (
          sumBy(summaries, (s) => s.kg) > cargosLimitByTruckOptions[v.truck_type].kg ||
          sumBy(summaries, (s) => s.cbm) > cargosLimitByTruckOptions[v.truck_type].cbm
        ) {
          ctx.addIssue({
            code: 'custom',
            message: priceSimulatorStrings.tooManyCargos,
            path: ['cargos'],
          });
        }
      },
    );
    if (v.has_pickups) {
      pipe(
        z
          .object({
            pickup_addresses: PriceSimulatorPickupAddress.array().min(1),
            cargos: z
              .object({
                pickup_address_index: z
                  .number()
                  .min(1, bookingInquiryStrings.atLeastOnePickupAddress),
              })
              .array(),
          })
          .safeParse(v),
        (e) => !e.success && e.error.issues.forEach(ctx.addIssue),
      );
    } else {
      pipe(
        z
          .object({
            cargos_port_name: z.string().min(1),
          })
          .safeParse(v),
        (e) => !e.success && e.error.issues.forEach(ctx.addIssue),
      );
    }
  });

export type PriceSimulatorForm = z.infer<typeof PriceSimulatorForm>;

export const priceSimulatorCase: PriceSimulatorForm = {
  has_pickups: false,
  cargos_port_name: 'SHANGHAI',
  receiver: {
    city_cd: '11201',
    pref: '埼玉県',
    zip_code: '',
    city: '川越市',
  },
  pickup_addresses: [
    {
      zip_code: '',
      city_name: '',
      dummy_address_cn: '',
    },
  ],
  cargos: [
    {
      tag: 'LWH',
      name: 'BOX',
      num: 56,
      length: 48,
      width: 37,
      height: 51,
      weight: 8.6,
      pickup_address_index: 0,
    },
    {
      tag: 'LWH',
      name: 'BOXBOXBOX BOXBOX',
      num: 56,
      length: 48,
      width: 37,
      height: 51,
      weight: 8.6,
      pickup_address_index: 0,
    },
  ],
  truck_type: 'any',
};

export const priceSimulator2Case2: PriceSimulatorForm = {
  has_pickups: false,
  cargos_port_name: 'SHANGHAI',
  receiver: {
    city_cd: '33213',
    pref: '岡山県',
    zip_code: '7090802',
    city: '赤磐市',
  },
  pickup_addresses: [
    {
      zip_code: '',
      city_name: '',
      dummy_address_cn: '',
    },
  ],
  cargos: [
    {
      tag: 'WV',
      name: 'GOLD SWING STICK',
      num: 38,
      total_volume: 1.03,
      total_weight: 639,
      pickup_address_index: 0,
    },
  ],
  truck_type: 'mix',
};

export const priceSimulator2Case3: PriceSimulatorForm = {
  has_pickups: false,
  cargos_port_name: 'GUANGDONG',
  receiver: {
    city_cd: '33213',
    pref: '岡山県',
    zip_code: '7090802',
    city: '赤磐市',
  },
  pickup_addresses: [
    {
      zip_code: '',
      city_name: '',
      dummy_address_cn: '',
    },
  ],
  cargos: [
    {
      tag: 'WV',
      name: 'GOLD SWING STICK',
      num: 38,
      total_volume: 1.03,
      total_weight: 639,
      pickup_address_index: 0,
    },
  ],
  truck_type: 'mix',
};

export const priceSimulator2Case4: PriceSimulatorForm = {
  has_pickups: true,
  cargos_port_name: 'SHANGHAI',
  receiver: {
    city_cd: '11201',
    pref: '埼玉県',
    zip_code: '',
    city: '川越市',
  },
  pickup_addresses: [
    {
      zip_code: '111111',
      city_name: '错误地址',
      dummy_address_cn: '',
    },
  ],
  cargos: [
    {
      tag: 'LWH',
      name: 'BOX',
      num: 56,
      length: 48,
      width: 37,
      height: 51,
      weight: 8.6,
      pickup_address_index: 1,
    },
  ],
  truck_type: 'any',
};

export const priceSimulatorCase5: PriceSimulatorForm = {
  has_pickups: true,
  cargos_port_name: 'SHANGHAI',
  receiver: { zip_code: '', pref: '東京都', city_cd: '13105', city: '文京区' },
  pickup_addresses: [
    {
      zip_code: '510000',
      city_name: 'Guangdong, Guangzhou',
      dummy_address_cn: '广东省, 广州',
    },
    {
      zip_code: '518102',
      city_name: 'Guangdong, Shenzhen',
      dummy_address_cn: '广东省, 深圳市',
    },
  ],
  cargos: [
    {
      name: 'BOX',
      num: 1,
      pickup_address_index: 1,
      tag: 'LWH',
      length: 48,
      width: 37,
      height: 51,
      weight: 8.6,
    },
    {
      name: 'HKR',
      num: 1,
      pickup_address_index: 1,
      tag: 'LWH',
      length: 48,
      width: 37,
      height: 51,
      weight: 8.6,
    },
    {
      name: 'BUG',
      num: 3,
      pickup_address_index: 2,
      tag: 'LWH',
      length: 100,
      width: 10,
      height: 10,
      weight: 3.4,
    },
  ],
  truck_type: 'any',
};

export const priceSimulatorRoutePlanSimulatorCase1: RoutePlanSimulator[] = [
  {
    route_plan: {
      id: 147,
      plan_id: 16,
      option_id: 'A',
      name: '深圳大阪航路',
      service_name: 'D2DSHENZHEN',
      receiving_city: 'GUANGDONG',
      pol: 'SHEKOU',
      pod: 'OSAKA',
      schedules: [
        {
          warehouse_closing_date: 2,
          etd: 0,
          eta: 5,
          pickup_date: 3,
        },
        {
          warehouse_closing_date: 4,
          etd: 4,
          eta: 2,
          pickup_date: 5,
        },
      ],
      receiving_warehouses: [
        {
          city: 'SHENZHEN',
          agency_id: 26,
          name: '深圳联合优选国际物流有限公司',
          name_en: 'FC STANDARD LOGISTICS CO.,LTD.',
          zipcode: '518109',
          address1: 'Guangdong, Shenzhen',
          address2: 'A102-D101, Floor 1, No. 310, Guiyue Rd',
          principal_name: 'Mr. Wany/May',
          phone: '18681481181/13530090427',
          memo: '深圳市龙华区观澜街道桂月路310号丰盛工业城宿舍楼一楼A102-D101',
        },
      ],
      export_warehouse: {
        city: 'SHENZHEN',
        agency_id: 26,
        name: '深圳联合优选国际物流有限公司',
        name_en: 'FC STANDARD LOGISTICS CO.,LTD.',
        zipcode: '518109',
        address1: 'Guangdong, Shenzhen',
        address2: 'A102-D101, Floor 1, No. 310, Guiyue Rd',
        principal_name: 'Mr. Wany/May',
        phone: '18681481181/13530090427',
        memo: '深圳市龙华区观澜街道桂月路310号丰盛工业城宿舍楼一楼A102-D101',
      },
      created_at: '2023-01-10T15:59:50.635738+09:00',
      updated_at: '2023-07-12T11:07:10.651488+09:00',
    },
    simulator: {
      receiver_city: '',
      delivery_days: 18,
      target_kg: 639,
      simulator_delivery_days: 1,
      simulator_pickup_days: 0,
      warehouse_date: '2023-07-23',
      pol_date: '2023-07-30',
      pod_date: '2023-08-04',
      grade: 0,
      invoice_details: [
        {
          expense_cost: 217,
          invoice_name: '中国輸出通関料',
          unit_num_type: 'B/L',
          currency: 'USD',
          unit_price: 55,
          unit_num: 1,
          excise_tax_type: '非課税',
          payer_mark: 'jp',
          country: 'cn',
          exchange_base_rate: 141.09,
          exchange_base_date: '2023-07-21',
        },
        {
          expense_cost: 221,
          invoice_name: '中国貿易代行サービス料',
          unit_num_type: 'B/L',
          currency: 'USD',
          unit_price: 40,
          unit_num: 0,
          excise_tax_type: '非課税',
          payer_mark: 'jp',
          country: 'cn',
          exchange_base_rate: 141.09,
          exchange_base_date: '2023-07-21',
        },
        {
          expense_cost: 223,
          invoice_name: '中国CFSチャージ',
          unit_num_type: 'M3',
          currency: 'USD',
          unit_price: 8,
          unit_num: 1.25,
          excise_tax_type: '非課税',
          payer_mark: 'jp',
          country: 'cn',
          exchange_base_rate: 141.09,
          exchange_base_date: '2023-07-21',
        },
        {
          expense_cost: 220,
          invoice_name: '中国集荷料金',
          unit_num_type: 'JOB',
          currency: 'JPY',
          unit_price: 0,
          unit_num: 1,
          excise_tax_type: '非課税',
          payer_mark: 'jp',
          country: 'cn',
          exchange_base_rate: 0,
          exchange_base_date: '',
        },
        {
          expense_cost: 214,
          invoice_name: 'D2D料金（課税分）',
          unit_num_type: 'JOB',
          currency: 'JPY',
          unit_price: 30668,
          unit_num: 1,
          excise_tax_type: '課税10%',
          payer_mark: 'jp',
          country: 'jp',
          exchange_base_rate: 0,
          exchange_base_date: '',
        },
        {
          expense_cost: 213,
          invoice_name: 'D2D料金（非課税分）',
          unit_num_type: 'JOB',
          currency: 'JPY',
          unit_price: 43548,
          unit_num: 1,
          excise_tax_type: '非課税',
          payer_mark: 'jp',
          country: 'jp',
          exchange_base_rate: 0,
          exchange_base_date: '',
        },
      ],
      pickups: [],
      baggage: {
        total_kg_num: 639,
        total_m3kg: 288,
        total_m3_num: 1.03,
        total_m3: 1.03,
        target_kg: 639,
        base: 'KG',
      },
      truck_options: [
        {
          price: {
            price: 10400,
            price2: 27685,
            clearance: {
              tax: 550,
              price: 19650,
              peak_season_surcharge: 0,
              service_charge_handring: 6050,
              service_charge_clearance: 13600,
            },
            import_tax: 8984,
            total_price: 77283,
            on_sea_price: 29948,
            shipping_tax: 3067,
            target_price: 33735,
            target_tax_price: 30668,
            non_target_tax_price: 43548,
          },
          lead_time: {
            delivery_leadtime: '1',
          },
          truck_type: 'mix',
        },
        {
          price: {
            price: 40000,
            price2: 62920,
            clearance: {
              tax: 550,
              price: 19650,
              peak_season_surcharge: 0,
              service_charge_handring: 6050,
              service_charge_clearance: 13600,
            },
            import_tax: 8984,
            total_price: 112518,
            on_sea_price: 29948,
            shipping_tax: 6270,
            target_price: 68970,
            target_tax_price: 62700,
            non_target_tax_price: 43548,
          },
          lead_time: {
            delivery_leadtime: '1',
          },
          truck_type: '4T',
        },
      ],
    },
  },
  {
    route_plan: {
      id: 134,
      plan_id: 7,
      option_id: 'A',
      name: '広東上海大阪航路',
      service_name: 'D2DSHENZHEN+',
      receiving_city: 'GUANGDONG',
      pol: 'SHANGHAI',
      pod: 'OSAKA',
      schedules: [
        {
          warehouse_closing_date: 0,
          etd: 5,
          eta: 1,
          pickup_date: 3,
        },
        {
          warehouse_closing_date: 4,
          etd: 2,
          eta: 4,
          pickup_date: 1,
        },
      ],
      receiving_warehouses: [
        {
          city: 'GUANGDONG',
          agency_id: 26,
          name: '賽博物流',
          name_en: 'Saibo Logistics Co., Ltd',
          zipcode: '510540',
          address1: 'Guangdong, Guangzhou, Baiyun, Jiahe, Taihe',
          address2: 'Baizuqiao Qijun Logistics Market D117-1119',
          principal_name: '',
          phone: '020-62659798',
          memo: '广州白雲区太和鎮百足橋淇駿物流市場D117-1119 TEL 020-62659798 36526147',
        },
      ],
      export_warehouse: {
        city: 'SHANGHAI',
        agency_id: 26,
        name: '上海領東物流有限公司',
        name_en: 'Shanghai Lingdong Logistics Co., Ltd.',
        zipcode: '201208',
        address1: 'Shanghai, Pudong',
        address2: 'No. 8, Lane 251, Shendong Rd',
        principal_name: 'Wang Yangjia',
        phone: '021-58902822',
        memo: '上海市申東路251弄8号 汪洋佳 TEL 021 58902822',
      },
      created_at: '2022-11-30T12:33:50.402538+09:00',
      updated_at: '2023-01-31T22:52:17.231410+09:00',
    },
    simulator: {
      receiver_city: '',
      delivery_days: 11,
      target_kg: 639,
      simulator_delivery_days: 1,
      simulator_pickup_days: 0,
      warehouse_date: '2023-07-23',
      pol_date: '2023-07-28',
      pod_date: '2023-07-31',
      grade: 0,
      invoice_details: [
        {
          expense_cost: 219,
          invoice_name: '中国倉庫入庫料',
          unit_num_type: 'B/L',
          currency: 'USD',
          unit_price: 24,
          unit_num: 1,
          excise_tax_type: '非課税',
          payer_mark: 'jp',
          country: 'cn',
          exchange_base_rate: 141.09,
          exchange_base_date: '2023-07-21',
        },
        {
          expense_cost: 217,
          invoice_name: '中国輸出通関料',
          unit_num_type: 'B/L',
          currency: 'USD',
          unit_price: 16,
          unit_num: 1,
          excise_tax_type: '非課税',
          payer_mark: 'jp',
          country: 'cn',
          exchange_base_rate: 141.09,
          exchange_base_date: '2023-07-21',
        },
        {
          expense_cost: 221,
          invoice_name: '中国貿易代行サービス料',
          unit_num_type: 'B/L',
          currency: 'USD',
          unit_price: 40,
          unit_num: 0,
          excise_tax_type: '非課税',
          payer_mark: 'jp',
          country: 'cn',
          exchange_base_rate: 141.09,
          exchange_base_date: '2023-07-21',
        },
        {
          expense_cost: 223,
          invoice_name: '中国CFSチャージ',
          unit_num_type: 'M3',
          currency: 'USD',
          unit_price: 5,
          unit_num: 2,
          excise_tax_type: '非課税',
          payer_mark: 'jp',
          country: 'cn',
          exchange_base_rate: 141.09,
          exchange_base_date: '2023-07-21',
        },
        {
          expense_cost: 220,
          invoice_name: '中国集荷料金',
          unit_num_type: 'JOB',
          currency: 'JPY',
          unit_price: 0,
          unit_num: 1,
          excise_tax_type: '非課税',
          payer_mark: 'jp',
          country: 'cn',
          exchange_base_rate: 0,
          exchange_base_date: '',
        },
        {
          expense_cost: 214,
          invoice_name: 'D2D料金（課税分）',
          unit_num_type: 'JOB',
          currency: 'JPY',
          unit_price: 30668,
          unit_num: 1,
          excise_tax_type: '課税10%',
          payer_mark: 'jp',
          country: 'jp',
          exchange_base_rate: 0,
          exchange_base_date: '',
        },
        {
          expense_cost: 213,
          invoice_name: 'D2D料金（非課税分）',
          unit_num_type: 'JOB',
          currency: 'JPY',
          unit_price: 48862,
          unit_num: 1,
          excise_tax_type: '非課税',
          payer_mark: 'jp',
          country: 'jp',
          exchange_base_rate: 0,
          exchange_base_date: '',
        },
      ],
      pickups: [],
      baggage: {
        total_kg_num: 639,
        total_m3kg: 288,
        total_m3_num: 1.03,
        total_m3: 1.03,
        target_kg: 639,
        base: 'KG',
      },
      truck_options: [
        {
          price: {
            price: 10400,
            price2: 27685,
            clearance: {
              tax: 550,
              price: 19650,
              peak_season_surcharge: 0,
              service_charge_handring: 6050,
              service_charge_clearance: 13600,
            },
            import_tax: 22880,
            total_price: 82597,
            on_sea_price: 35262,
            shipping_tax: 3067,
            target_price: 33735,
            target_tax_price: 30668,
            non_target_tax_price: 48862,
          },
          lead_time: {
            delivery_leadtime: '1',
          },
          truck_type: 'mix',
        },
        {
          price: {
            price: 40000,
            price2: 62920,
            clearance: {
              tax: 550,
              price: 19650,
              peak_season_surcharge: 0,
              service_charge_handring: 6050,
              service_charge_clearance: 13600,
            },
            import_tax: 22880,
            total_price: 117832,
            on_sea_price: 35262,
            shipping_tax: 6270,
            target_price: 68970,
            target_tax_price: 62700,
            non_target_tax_price: 48862,
          },
          lead_time: {
            delivery_leadtime: '1',
          },
          truck_type: '4T',
        },
      ],
    },
  },
];
